// IndentEntry — auto-lists store products below their minimum stock and lets
// the user raise a Pending indent / purchase order for them. Creating an indent
// does NOT change stock (that happens on "Receive" in the Pending list screen).
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import config from '../../config';
import { requireUserId } from './userSession';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Companycurrency = company?.currency ?? '';
const token = sessionStorage.getItem('token');

export default function IndentEntry() {
  const [rows, setRows] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const showSnack = (msg, sev = 'success') =>
    setSnack({ open: true, msg, sev });

  const loadData = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const [lowRes, supRes] = await Promise.all([
        fetch(`${config.API_BASE_URL}/indent/lowstock/${company.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => (r.ok ? r.json() : [])),
        fetch(`${config.API_BASE_URL}/home/GetSuppliers/${company.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => (r.ok ? r.json() : [])),
      ]);
      setAllSuppliers(Array.isArray(supRes) ? supRes : []);
      setRows(
        (Array.isArray(lowRes) ? lowRes : []).map((p) => ({
          ...p,
          qty: '',
          supplierId: '',
          supplierName: '',
          price: '',
          remark: '',
        })),
      );
    } catch (err) {
      console.error('Indent load error:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Per-product supplier options = purchase-history suppliers (with last rate)
  // merged with every active supplier, deduped by id.
  const supplierOptions = (row) => {
    const map = new Map();
    (row.suppliers || []).forEach((s) =>
      map.set(s.supplierId, {
        id: s.supplierId,
        name: s.supplierName,
        lastRate: s.lastRate,
        lastDate: s.lastDate,
      }),
    );
    allSuppliers.forEach((s) => {
      const id = s.id ?? s.Id;
      const name = s.supplierName ?? s.SupplierName ?? s.name ?? '';
      if (!map.has(id))
        map.set(id, { id, name, lastRate: null, lastDate: null });
    });
    return Array.from(map.values());
  };

  const supplierLabel = (o) => {
    if (!o) return '';
    if (o.lastRate != null && Number(o.lastRate) > 0) {
      const d = o.lastDate ? new Date(o.lastDate).toLocaleDateString('en-GB') : '';
      return `${o.name} — ${Companycurrency}${Number(o.lastRate).toFixed(2)}${d ? ` (${d})` : ''}`;
    }
    return o.name;
  };

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const onSupplierChange = (idx, opt) => {
    if (!opt) {
      updateRow(idx, { supplierId: '', supplierName: '' });
      return;
    }
    const row = rows[idx];
    const autoPrice =
      opt.lastRate != null && Number(opt.lastRate) > 0
        ? Number(opt.lastRate)
        : row.lastPurchasePrice || '';
    updateRow(idx, {
      supplierId: opt.id,
      supplierName: opt.name,
      // Only auto-fill price if the user hasn't typed one yet.
      price: row.price === '' ? autoPrice : row.price,
    });
  };

  const handleSave = async () => {
    const lines = rows
      .filter((r) => Number(r.qty) > 0)
      .map((r) => ({
        ProductId: r.productId,
        ProductName: r.productName,
        SupplierId: Number(r.supplierId) || 0,
        SupplierName: r.supplierName || '',
        Qty: Number(r.qty),
        Price: Number(r.price) || 0,
        Remark: r.remark || '',
      }));

    if (lines.length === 0) {
      showSnack('Enter quantity for at least one product.', 'warning');
      return;
    }
    const missingSupplier = lines.find((l) => !l.SupplierId);
    if (missingSupplier) {
      showSnack(
        `Select a supplier for ${missingSupplier.ProductName}.`,
        'warning',
      );
      return;
    }

    const uid = requireUserId();
    if (!uid) return;

    setSaving(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/indent/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CompanyId: company.id,
          CreatedBy: uid,
          Lines: lines,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showSnack(data?.error || 'Failed to create indent.', 'error');
        return;
      }
      if (data?.success === false) {
        showSnack(data?.message || 'Nothing was created.', 'warning');
        return;
      }
      const skippedNote =
        data?.skipped?.length > 0
          ? ` (${data.skipped.length} skipped — already pending)`
          : '';
      showSnack(
        `Indent ${data.orderNo} created — ${data.created} item(s)${skippedNote}.`,
        'success',
      );
      loadData();
    } catch (err) {
      console.error('Indent save error:', err);
      showSnack('Something went wrong.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        flexWrap='wrap'
        gap={1}
        mb={2}>
        <Box>
          <Typography variant='h5' fontWeight={800} sx={{ color: '#0a2540' }}>
            🧾 Indent / Purchase Order
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Products below their minimum stock — enter qty, pick a supplier and
            save to raise a pending order.
          </Typography>
        </Box>
        <Box display='flex' gap={1}>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}>
            Refresh
          </Button>
          <Button
            variant='contained'
            color='success'
            startIcon={
              saving ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSave}
            disabled={saving || loading || rows.length === 0}>
            {saving ? 'Saving...' : 'Save Indent'}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity='success'>
          All products are above their minimum stock — nothing to indent.
        </Alert>
      ) : (
        <Paper sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  {[
                    'Product',
                    'Current',
                    'Min',
                    'Required Qty',
                    'Supplier',
                    `Price (${Companycurrency})`,
                    'Remark',
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#0a2540',
                        color: '#fff',
                        fontWeight: 'bold',
                      }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={row.productId} hover>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>
                      {row.productName}
                      {row.unit ? (
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ ml: 0.5 }}>
                          ({row.unit})
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={Number(row.currentStock)}
                        size='small'
                        color='error'
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell>{Number(row.minStock)}</TableCell>
                    <TableCell sx={{ width: 110 }}>
                      <TextField
                        type='number'
                        size='small'
                        value={row.qty}
                        onChange={(e) =>
                          updateRow(idx, { qty: e.target.value })
                        }
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 230 }}>
                      <Autocomplete
                        size='small'
                        options={supplierOptions(row)}
                        getOptionLabel={supplierLabel}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={
                          row.supplierId
                            ? supplierOptions(row).find(
                                (o) => o.id === row.supplierId,
                              ) || null
                            : null
                        }
                        onChange={(_, opt) => onSupplierChange(idx, opt)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder='Select supplier'
                          />
                        )}
                        sx={{ width: 220 }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 110 }}>
                      <TextField
                        type='number'
                        size='small'
                        value={row.price}
                        onChange={(e) =>
                          updateRow(idx, { price: e.target.value })
                        }
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TextField
                        size='small'
                        placeholder='Remark'
                        value={row.remark}
                        onChange={(e) =>
                          updateRow(idx, { remark: e.target.value })
                        }
                        sx={{ width: 150 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
